<template>
  <div>
    <b-form-group class="mb-0">
      <template slot="label">
        <label for="visibility-setting" class="top-label font-weight-bold">Visibility Setting</label>
        <i class="ic-tooltip ml-2 mr-5" id="tooltip-visibility-setting"></i>
        <b-popover target="tooltip-visibility-setting" triggers="hover"
          content='The “Cancel” button will automatically disappear from your customer-facing website and/or mobile app after the time period you configured in the settings. This action will prevent unintended cancellations and streamlines the order fulfilment process.'>
        </b-popover>
      </template>
    </b-form-group>
    <b-form-group label="Display within" label-class="top-label">
      <b-row>
        <b-col>
          <b-dropdown id="time-type-dropdown" class="w-100"
            :toggle-class="['w-100 d-inline-flex justify-content-between align-items-center', { 'empty-text': form.disable_cancel }]"
            :disabled="form.disable_cancel"
            menu-class="p-0" :text="timeTypeDisplay">
            <b-dropdown-item v-for="({ text, value }, typeIndex) in typesDropdown" :key="`time-type-${typeIndex}`"
              @click="setTimeType(value)">
              {{ text }}
            </b-dropdown-item>
          </b-dropdown>
          <b-form-invalid-feedback :force-show="$v.type.$invalid">
            <div v-for="typeError in $v.type.$errors" :key="typeError.$uid">
              {{ typeError.$message }}
            </div>
          </b-form-invalid-feedback>
        </b-col>
        <b-col>
          <b-dropdown id="time-value-dropdown" class="w-100"
            :toggle-class="['w-100 d-inline-flex justify-content-between align-items-center', { 'empty-text': form.disable_cancel }]"
            :disabled="form.disable_cancel"
            menu-class="p-0" :text="valueDisplay">
            <b-dropdown-item v-for="({ text, value }, valueIndex) in valuesDropdownOptions"
              :key="`time-value-${valueIndex}`" @click="setTimeValue(value)">
              {{ text }}
            </b-dropdown-item>
          </b-dropdown>
          <b-form-invalid-feedback :force-show="$v.disable_cancel_seconds.$invalid">
            <div v-for="secValError in $v.disable_cancel_seconds.$errors" :key="secValError.$uid">
              {{ secValError.$message }}
            </div>
          </b-form-invalid-feedback>
        </b-col>
      </b-row>
      <b-form-checkbox v-model="form.disable_cancel">
        <span class="top-label">Always show</span>
      </b-form-checkbox>
    </b-form-group>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, getCurrentInstance, onMounted, onBeforeUnmount, nextTick } from "@vue/composition-api";
import { useVuelidate } from "@vuelidate/core";
import { requiredIf, helpers } from "@vuelidate/validators";

export default defineComponent({
  props: ["visibilitySettings"],
  setup(props, { root }) {
    const currentInstance = getCurrentInstance();

    onMounted(() => {
      root.$on("validateCancelOrderVisibilitySettings", () => validateVisibilitySettings());

      nextTick(() => {
        const { disable_cancel, disable_cancel_seconds } = { ...props.visibilitySettings };
        form.value.disable_cancel = !(disable_cancel / 1);
        setTimeValue(disable_cancel_seconds);
      });
    });

    onBeforeUnmount(() => {
      root.$off("validateCancelOrderVisibilitySettings");
    });

    const form = ref({
      disable_cancel: true,
      disable_cancel_seconds: null,
      type: null
    });

    const secondsConversion = {
      minutes: 60,
      hours: 3600
    };

    const minutesValues = [
      { text: 5, value: 5 * secondsConversion.minutes },
      { text: 10, value: 10 * secondsConversion.minutes },
      { text: 15, value: 15 * secondsConversion.minutes },
      { text: 30, value: 30 * secondsConversion.minutes }
    ];

    const hoursValues = [
      { text: 1, value: 1 * secondsConversion.hours },
      { text: 1.5, value: 1.5 * secondsConversion.hours },
      { text: 2, value: 2 * secondsConversion.hours },
      { text: 2.5, value: 2.5 * secondsConversion.hours }
    ];

    const typesDropdown = [
      { text: "Minutes", value: "minutesValues" },
      { text: "Hour", value: "hoursValues" }
    ];

    const valuesDropdownOptions = computed(() => {
      if (!form.value.type && !form.value.disable_cancel_seconds) return minutesValues;
      else if (form.value.type) return currentInstance.proxy[form.value.type];
      return [];
    });

    const timeTypeDisplay = computed(() => {
      if (!form.value.type) return "-";
      else return typesDropdown.find(({ value }) => value === form.value.type).text;
    });

    const valueDisplay = computed(() => {
      const textDisplay = valuesDropdownOptions.value.find(({ value }) => value === form.value.disable_cancel_seconds);

      if (textDisplay) return textDisplay.text.toString();
      else return "-";
    });

    const requiredFieldMessage = "This field is required.";

    const rules = {
      type: {
        required: helpers.withMessage(requiredFieldMessage, requiredIf(() => !form.value.disable_cancel))
      },
      disable_cancel_seconds: {
        required: helpers.withMessage(requiredFieldMessage, requiredIf(() => !form.value.disable_cancel))
      }
    };

    const $v = useVuelidate(rules, form);

    const validateVisibilitySettings = () => {
      if (!form.value.type && !form.value.disable_cancel_seconds) form.value.disable_cancel = true;

      else $v.value.$touch();
    };

    const setTimeType = (type) => {
      if (type !== form.value.type) {
        form.value.type = type;
        form.value.disable_cancel_seconds = null;

        if (type) form.value.disable_cancel = false;
      }
    };

    const setTimeValue = (secs) => {
      if (secs) form.value.disable_cancel_seconds = secs;

      const minVals = minutesValues.map(({ value }) => value);
      const hrsVals = hoursValues.map(({ value }) => value);

      if (
        minVals.includes(secs) &&
        form.value.type !== "minutesValues"
      ) form.value.type = "minutesValues";
      else if (
        hrsVals.includes(secs) &&
        form.value.type !== "hoursValues"
      ) form.value.type = "hoursValues";

      if (secs) form.value.disable_cancel = false;
    };

    watch(() => form.value.disable_cancel, (value) => {
      if (value) {
        form.value.type = null;
        form.value.disable_cancel_seconds = null;
      }
    });

    watch(() => form.value.disable_cancel, () => $v.value.$reset());

    return {
      form,
      minutesValues,
      hoursValues,
      typesDropdown,
      valuesDropdownOptions,
      timeTypeDisplay,
      valueDisplay,
      $v,
      setTimeType,
      setTimeValue
    }
  }
});
</script>

<style lang="scss" scoped>
.top-label {
  position: relative;
  top: -1px;
}
</style>

<style lang="scss">
#time-type-dropdown,
#time-value-dropdown {
  &>button {
    background: transparent;
    border-color: #ced4da;
    color: #222D37;
    box-shadow: none;

    &::after {
      content: "";
      border: 0;
      width: 11px;
      height: 6px;
      background: url('~@/assets/images/caret-down.svg') no-repeat;
    }

    &.empty-text {
      background: #F8F9F9;
      border-color: transparent;
      color: #C2C8CC;

      &::after {
        filter: invert(1);
      }
    }
  }

  .dropdown-menu {
    width: 100%;
  }
}
</style>
